import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import {
  DocumentState,
  DocumentStateBase,
} from "@/services/backend/documents/types/state";
import {
  documentStateActionLabel,
  documentStateLabel,
  isImmutable,
} from "@/services/backend/documents/lib/document-state";
import { DocumentStateIcon } from "@/shared/bsm/core/document/components/ui/document-state-icon";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";

/**
 * UpdateStateRequest is the request every document takes to move to another
 * [DocumentState]. The state is recorded for the requesting user, which the
 * backend policy enforces.
 */
export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

type UpdateStateMutation = () => readonly [
  (request: UpdateStateRequest) => void,
  {
    isLoading: boolean;
    error?: unknown;
    isSuccess: boolean;
    reset: () => void;
  },
];

/**
 * allowedTransitions returns the states a document can move to. A written
 * down document is final, see [document.StateBase.CanTransitionTo] in the
 * backend.
 */
function allowedTransitions(state: DocumentState): DocumentState[] {
  switch (state) {
    case DocumentState.Draft:
      return [DocumentState.Ready, DocumentState.Immutable];
    case DocumentState.Ready:
      return [DocumentState.Draft, DocumentState.Immutable];
    default:
      return [];
  }
}

/**
 * DocumentStateDropdown shows the state of a document and offers the
 * transitions it allows. Writing a document down cannot be undone and is
 * confirmed in a dialog.
 *
 * A written down document allows no transition anymore, its state is shown
 * as a disabled button.
 */
export function DocumentStateDropdown({
  document,
  mutation,
  onError,
  immutableDescription = t(
    "Das Dokument wird festgeschrieben und kann danach nicht mehr geändert werden.",
  ),
}: {
  document: DocumentStateBase & { id: string };
  mutation: UpdateStateMutation;
  /**
   * onError receives the error of a rejected transition, except a
   * validation or bsm.ErrDocumentNotReady error, which is written into the
   * nearest ErrValidationFailedContextProvider. Without it every other error is
   * shown as a toast.
   */
  onError?: (error: unknown) => void;
  immutableDescription?: ReactNode;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { write: writeValidationError } = useErrValidationFailedContext();
  const [confirming, setConfirming] = useState(false);
  const [mutate, { isLoading, error, isSuccess, reset }] = mutation();

  useEffect(() => {
    if (isSuccess) {
      reset();
      setConfirming(false);
    }
  }, [isSuccess, reset]);

  // The dropdown stays mounted while the user works on the document, so an
  // error is reported once. Reporting it on every render would restore the
  // errors the user is currently resolving, e.g. the fields a document is
  // missing to be ready.
  const reportedError = useRef<unknown>(null);
  useEffect(() => {
    if (!error) {
      reportedError.current = null;
      return;
    }

    if (reportedError.current === error) {
      return;
    }
    reportedError.current = error;

    // A document which is not ready for the customer names the fields it is
    // missing. They belong on the page, where the user resolves them, not in a
    // toast which cannot show them. Without a provider the error is toasted.
    if (writeValidationError(error)) {
      return;
    }

    if (onError) {
      onError(error);
      return;
    }

    toast({ ...parseRTKQueryError(error, false), variant: "destructive" });
  }, [error, onError, writeValidationError, toast]);

  const updateState = (state: DocumentState) => {
    if (isLoading) {
      return;
    }

    mutate({ id: document.id, state, stateSetBy: user?.id || "" });
  };

  if (isImmutable(document)) {
    return (
      <Button variant="outline" disabled>
        <DocumentStateIcon state={document.state} className="mr-2 h-4 w-4" />
        <span>{documentStateLabel(document.state)}</span>
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={isLoading}>
            <DocumentStateIcon
              state={document.state}
              className="mr-2 h-4 w-4"
            />
            <span>{documentStateLabel(document.state)}</span>
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {allowedTransitions(document.state).map((state) => (
            <DropdownMenuItem
              key={state}
              onClick={() => {
                // Drop the error of an earlier attempt, so the dialog and
                // the page show what this attempt reports.
                reset();
                if (state === DocumentState.Immutable) {
                  setConfirming(true);
                  return;
                }
                updateState(state);
              }}
            >
              <DocumentStateIcon state={state} className="mr-2 h-4 w-4" />
              {documentStateActionLabel(state)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Dokument festschreiben?")}</DialogTitle>
            <DialogDescription>{immutableDescription}</DialogDescription>
          </DialogHeader>
          <RTKQueryErrorAlert error={error} />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("Abbrechen")}</Button>
            </DialogClose>
            <Button
              disabled={isLoading}
              onClick={() => updateState(DocumentState.Immutable)}
            >
              {t("Festschreiben")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
